import React, { useEffect, useRef } from 'react';
import '../Pages/styles.css'; // Import the prefixed CSS as requested

// --- Import FontAwesomeIcon Component ---
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

// --- Import ALL Necessary Icons from Font Awesome (Updated for Master Planning Content) ---
import {
    // Core Concepts & General
    faSitemap,          // Master Planning / Campus Design
    faBuilding,         // Facility / Infrastructure / Architecture
    faBalanceScale,     // Balancing Art & Science / Considerations
    faMicroscope,       // Science / Technical / Analysis
    faPalette,          // Art / Creativity / Vision / Design
    faCheckCircle,      // Quality / Compliance / Success
    faTasks,            // Functionality / Operations / Efficiency
    faHeartPulse,       // Healing Environments / Patient Satisfaction
    faUsersCog,         // Team / Expertise / Stakeholders
    faDraftingCompass,  // Design / Blueprints
    faClipboardList,    // Assessment / Planning / Roadmap
    // 4-Phase Process Icons
    faLightbulb,        // Phase I: Strategic Planning / Vision
    faMapMarkedAlt,     // Phase II: Site Planning / Location
    faProjectDiagram,   // Phase III: Interdepartmental Planning / Flow
    faTools,            // Phase IV: Physical Setup / Construction / Evaluation
    // Keep other potentially useful icons if needed
    // Icons likely unused from previous versions:
    /*
     faCalculator, faMoneyCheckAlt, faFileInvoiceDollar, faExchangeAlt, faChartPie, faReceipt,
     faArrowsAltH, faFileExcel, faTachometerAlt, faCogs, faLandmark, faSearch, faFileSignature,
     faPlayCircle, faBinoculars, faSyncAlt, faUserTie
    */
} from '@fortawesome/free-solid-svg-icons'; // Using free solid icons


// --- Intersection Observer Hooks (No changes needed in hook logic) ---
const useIntersectionObserverTitle = (options) => {
    const containerRef = useRef(null);
    const [isVisible, setIsVisible] = React.useState(false);
    const callbackFunction = (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting) setIsVisible(true);
    };
    useEffect(() => {
        const observer = new IntersectionObserver(callbackFunction, options);
        const currentRef = containerRef.current;
        if (currentRef) observer.observe(currentRef);
        return () => { if (currentRef) observer.unobserve(currentRef); };
    }, [containerRef, options]);
    return [containerRef, isVisible];
};

const useIntersectionObserverElements = (options) => {
    useEffect(() => {
        const observerCallback = (entries, observer) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    const target = entry.target;
                    const parent = target.parentElement;
                    const siblings = parent ? Array.from(parent.children).filter(el => el.classList.contains('cliniqor-fade-in-up')) : [target];
                    const index = siblings.indexOf(target);
                    const staggerIndex = index >= 0 ? index : 0;

                    target.style.setProperty('--item-index', staggerIndex % 8); // Keep stagger logic
                    target.classList.add('cliniqor-visible'); // Use prefixed class
                    observer.unobserve(target);
                }
            });
        };
        const observer = new IntersectionObserver(observerCallback, options);
        const targets = document.querySelectorAll('.cliniqor-fade-in-up');
        targets.forEach(target => observer.observe(target));
        return () => targets.forEach(target => { if (target) observer.unobserve(target); });
    }, [options]);
};
// --- End Observer Hooks ---


function App() {

    // --- Observer Setup for Titles (Adjusted Refs) ---
    const titleObserverOptions = { threshold: 0.2 };
    const [title1Ref, title1Visible] = useIntersectionObserverTitle(titleObserverOptions); // Science of Planning
    const [title2Ref, title2Visible] = useIntersectionObserverTitle(titleObserverOptions); // Art of Planning
    const [title3Ref, title3Visible] = useIntersectionObserverTitle(titleObserverOptions); // The Balance / Expertise
    const [title4Ref, title4Visible] = useIntersectionObserverTitle(titleObserverOptions); // The Planning Process (4 Phases)

    // --- Observer Setup for General Fade-In Elements ---
    useIntersectionObserverElements({ threshold: 0.1 });

    // Choose a relevant hero background image for planning/design/architecture
    const heroBgImage = 'https://images.unsplash.com/photo-1606744824163-985d376605aa?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1920&q=80'; // Architecture/blueprint image

  return(
    // Use prefixed base wrapper class
    <div className="cliniqor-app-wrapper">

      {/* --- HERO SECTION --- */}
      <header
        className="cliniqor-hero-section" // Prefixed
        style={{ backgroundImage: `url(${heroBgImage})` }}
      >
        <div className="cliniqor-container"> {/* Prefixed */}
          <div className="cliniqor-hero-content"> {/* Prefixed */}
            {/* --- NEW HERO CONTENT --- */}
            <h1>Healthcare Campus Master Planning</h1>
            <p className="cliniqor-sub-heading">
              A Comprehensive Approach by Cliniqor for Sustainable Healthcare Success
            </p>
            <p className="cliniqor-intro-text">
              We design sustainable, adaptable healthcare environments balancing functionality and future readiness, ensuring long-term success through a strategic blend of science and art in master planning.
            </p>
            {/* --- END NEW HERO CONTENT --- */}
          </div>
        </div>
      </header>

      <main>
        {/* --- SCIENCE OF MASTER PLANNING --- */}
        <section className="cliniqor-section cliniqor-section-first cliniqor-section-bg-gradient cliniqor-intro-section">
          <div className="cliniqor-container">
            <div ref={title1Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title1Visible ? 'cliniqor-visible' : ''}`}>
              <h2>The Science of Healthcare Campus Master Planning</h2>
              <p>Systematic studies and organized knowledge for operational excellence.</p>
            </div>
             <div className="cliniqor-intro-grid"> {/* Keeping grid structure */}
                 <div className="cliniqor-intro-text-content cliniqor-fade-in-up">
                     {/* --- NEW INTRO CONTENT --- */}
                     <p>Ensures thoughtful consideration of operational needs through adherence to industry standards, codes, and design principles. It involves integrating functional relationships across departments (emergency, surgery, wards, diagnostics) based on workflow dynamics.</p>
                     <p>Designs must comply with local, national, and international regulations (accessibility, infection control, safety, sustainability) to create a patient-centered environment optimizing efficiency.</p>
                     {/* --- END NEW INTRO CONTENT --- */}
                 </div>
             </div>
          </div>
        </section>

        {/* --- ART OF MASTER PLANNING --- */}
        <section className="cliniqor-section cliniqor-features-section cliniqor-section-bg-light">
          <div className="cliniqor-container">
            <div ref={title2Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title2Visible ? 'cliniqor-visible' : ''}`}>
              {/* --- NEW SECTION TITLE --- */}
              <h2>The Art of Healthcare Campus Master Planning</h2>
              <p>Creativity, innovation, and crafting a vision for the future.</p>
              {/* --- END NEW SECTION TITLE --- */}
            </div>
             <div className="cliniqor-intro-grid"> {/* Use intro grid for consistency */}
                 <div className="cliniqor-intro-text-content cliniqor-fade-in-up">
                     {/* --- NEW INTRO CONTENT --- */}
                     <p>Goes beyond basic functional requirements to embrace the human experience within the hospital environment. Architectural design should facilitate smooth flow, comfort, and healing through elements like natural light, materials, and colors.</p>
                     <p>The art involves anticipating future growth, technological advancements, and changes in healthcare delivery models, ensuring the design positively influences patient care and staff performance.</p>
                     {/* --- END NEW INTRO CONTENT --- */}
                 </div>
             </div>
          </div>
        </section>

        {/* --- THE BALANCE & EXPERTISE --- */}
        <section className="cliniqor-section cliniqor-section-bg-gradient cliniqor-intro-section"> {/* Use gradient again */}
          <div className="cliniqor-container">
             <div ref={title3Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title3Visible ? 'cliniqor-visible' : ''}`}>
                 <h2>The Balance: Blending Science and Art for Success</h2>
                 <p>Fusing function with environments that foster healing and innovation.</p>
             </div>
             <div className="cliniqor-text-content cliniqor-fade-in-up">
                 <p>The best designs balance scientific needs and artistic vision. A well-planned campus is operationally efficient and emotionally supportive. This requires domain expertise, experience, and understanding of future trends (technology, patient expectations, regulations).</p>
                 <p>Cliniqor's dedicated team (planners, engineers, architects, capability experts) collaborates to bring visions to life, offering end-to-end services ensuring facilities are built to last and meet community needs.</p>
             </div>
          </div>
        </section>


        {/* --- THE PLANNING PROCESS (Detailed Planning Section - 4 Phases) --- */}
        <section className="cliniqor-section cliniqor-section-bg-texture cliniqor-detailed-planning-section"> {/* Kept existing classes */}
          <div className="cliniqor-container">
             <div ref={title4Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title4Visible ? 'cliniqor-visible' : ''}`}>
              {/* --- NEW SECTION TITLE --- */}
              <h2>The Healthcare Campus Master Planning Process by Cliniqor</h2>
               <p>A structured 4-phase approach ensuring strategic alignment and operational excellence.</p>
               {/* --- END NEW SECTION TITLE --- */}
            </div>
            {/* Using Planning Items for the 4 Phases with Summarized Detail */}
            <div className="cliniqor-planning-items-wrapper">
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faLightbulb} /></div>
                    <div className="cliniqor-content"><h4>Phase I: Strategic Planning & Assessment</h4> <p>Aligning stakeholder vision with blueprint; conducting business assessment (financials, operations); studying trends; initial space planning & energy conservation; staffing needs assessment.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faMapMarkedAlt} /></div>
                    <div className="cliniqor-content"><h4>Phase II: Conceptual Site Planning</h4> <p>Site assessment (location, accessibility, environment); analyzing patient/visitor flow; developing conceptual drawings, layouts, and facility design concepts considering zoning/legal aspects.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faProjectDiagram} /></div>
                    <div className="cliniqor-content"><h4>Phase III: Interdepartmental Planning & Design</h4> <p>Planning internal layout for functional flow; identifying department/room locations (ER, OT, ICU, support); planning vertical transport & corridors; prioritizing critical zone placement.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faTools} /></div>
                    <div className="cliniqor-content"><h4>Phase IV: Physical Set-up, Evaluation & Recommendations</h4> <p>Overseeing construction per specs/standards; evaluating progress, identifying issues/constraints; recommending modifications; providing final operational recommendations.</p> </div>
                 </div>
            </div>
          </div>
        </section>

       {/* Removed other specific sections */}

      </main>

      {/* --- FOOTER CTA --- */}
      <footer className="cliniqor-section cliniqor-section-narrow cliniqor-footer-cta">
        <div className="cliniqor-container cliniqor-fade-in-up">
          {/* --- UPDATED FOOTER CONTENT --- */}
          <h2>Create a Future-Ready Healthcare Campus</h2>
          <p>Cliniqor delivers well-designed, functional, sustainable, and adaptable healthcare campuses. Our master planning blends scientific precision with creative vision for long-term value to patients and providers.</p>
          <a href="#contact" className="cliniqor-cta-button">Start Your Master Plan Today</a>
          {/* --- END UPDATED FOOTER CONTENT --- */}
        </div>
      </footer>
    </div> // End cliniqor-app-wrapper
  );
}

export default App;